/**
 * useNetworkCapacity
 *
 * Aggregates per-location test capacity (modem counts, detected networks,
 * capabilities) from the edge devices the caller is allowed to see, and splits
 * each location into "SimCheck network" (ownerOrgId === null) vs "yours"
 * (ownerOrgId === active org).
 *
 * Tenants never see shared hardware AS DEVICES — only as the capacity numbers
 * this hook produces. That boundary is the reason the aggregation lives here
 * rather than on a device list: the Edges page renders locations, not the
 * devices behind them. See plans/edges-page-shared-vs-owned.md.
 *
 * Extracted from Locations.tsx so the admin Locations manager and the tenant
 * Edges page compute capacity identically.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AggregatedCapabilities, Country, Location, Region } from '@simcheck/shared-types';
import { resolveModemModelByReportedName } from '@simcheck/shared-types';
import {
  getFullHierarchicalLocations,
  filterLocationsByTenancy,
} from '../services/hierarchicalLocationService';
import { edgeDeviceService } from '../services/edgeDeviceService';
import { getAllModemModels } from '../services/modemModelService';
import { useAuth } from '../contexts/AuthContext';
import { useDeviceVisibility, filterDevicesByTier } from './useDeviceVisibility';

export interface NetworkInfo {
  networkId: string;
  networkName: string;
  mcc: string;
  mnc: string;
}

export type LocationOwnership = 'simcheck' | 'yours';

export interface CapacityLocation extends Omit<Location, 'availableNetworks'> {
  regionName: string;
  countryName: string;
  countryFlag: string;
  aggregatedCapabilities?: AggregatedCapabilities;
  availableNetworks?: NetworkInfo[];
  /** 'yours' when the location is owned by the active org, else 'simcheck'. */
  ownership: LocationOwnership;
}

export interface NetworkCapacity {
  locations: CapacityLocation[];
  regions: Region[];
  countries: Country[];
  /** Unfiltered hierarchy — create/edit forms need regions with no location yet. */
  allRegions: Region[];
  allCountries: Country[];
  /** Location ids that have at least one visible device. */
  locationsWithDevices: Set<string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const TECH_ORDER: Record<string, number> = {
  '5G': 1,
  '4G': 2,
  'LTE-M': 3,
  'NB-IoT': 4,
  '3G': 5,
  '2G': 6,
};

interface LocationAggregate {
  totalModems: number;
  availableModems: number;
  networks: Map<string, NetworkInfo>;
  technologies: Set<string>;
  features: Set<string>;
}

export function useNetworkCapacity(): NetworkCapacity {
  const { isSuperAdmin, activeOrganization } = useAuth();
  const visibilityFlags = useDeviceVisibility();
  const activeOrgId = activeOrganization?.id;

  const [locations, setLocations] = useState<CapacityLocation[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [allRegions, setAllRegions] = useState<Region[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [locationsWithDevices, setLocationsWithDevices] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [data, devices, modemModels] = await Promise.all([
        getFullHierarchicalLocations(),
        edgeDeviceService.getDevices().catch((err) => {
          console.error('[useNetworkCapacity] Failed to fetch devices:', err);
          return [];
        }),
        getAllModemModels(true).catch((err) => {
          console.error('[useNetworkCapacity] Failed to fetch modem models:', err);
          return [];
        }),
      ]);

      const modemModelCapabilities = new Map<string, Record<string, any>>();
      for (const model of modemModels) {
        if (model.id && model.capabilities) {
          modemModelCapabilities.set(model.id, model.capabilities as Record<string, any>);
        }
      }

      const visibleDevices = filterDevicesByTier(devices, visibilityFlags);

      const locationData = new Map<string, LocationAggregate>();

      for (const device of visibleDevices) {
        if (!device.locationId) continue;

        if (!locationData.has(device.locationId)) {
          locationData.set(device.locationId, {
            totalModems: 0,
            availableModems: 0,
            networks: new Map(),
            technologies: new Set(),
            features: new Set(),
          });
        }

        const locData = locationData.get(device.locationId)!;

        // A modem is available if its device is online AND the modem is not
        // explicitly disconnected/error.
        const deviceModems = device.modems || [];
        locData.totalModems += deviceModems.length;
        const deviceIsOnline = device.status === 'online';
        locData.availableModems += deviceModems.filter((m) => {
          if (!deviceIsOnline) return false;
          return m.status !== 'disconnected' && m.status !== 'error';
        }).length;

        for (const modem of deviceModems) {
          if (modem.availableNetworks && Array.isArray(modem.availableNetworks)) {
            for (const network of modem.availableNetworks) {
              const key = `${network.mcc || ''}-${network.mnc || ''}-${network.operator_name || ''}`;
              if (!locData.networks.has(key)) {
                locData.networks.set(key, {
                  networkId: key,
                  networkName: network.operator_name || 'Unknown',
                  mcc: network.mcc || '',
                  mnc: network.mnc || '',
                });
              }
            }
          }

          // Capability resolution, in order of authority:
          //   1. per-modem overrides
          //   2. the linked catalog model (`modemModelId`)
          //   3. the model STRING the edge reported
          // (3) exists because this reads the legacy `devices.modems[]` array,
          // which carries `model` but neither capabilities nor a model link —
          // the real link lives on `devices/{id}/known_modems/{imei}` and never
          // reaches this payload, which is why every location read "No data".
          let caps = modem.capabilities as Record<string, any> | undefined;
          if (!caps || Object.keys(caps).length === 0) {
            const linked = modem.modemModelId
              ? modemModelCapabilities.get(modem.modemModelId)
              : undefined;
            if (linked) {
              caps = linked;
            } else {
              const matched = resolveModemModelByReportedName(
                (modem as any).model,
                modemModels as any[],
              );
              if (matched?.capabilities) caps = matched.capabilities as Record<string, any>;
            }
          }

          if (caps && Object.keys(caps).length > 0) {
            const getCapValue = (key: string): boolean => {
              const cap = caps![key];
              if (!cap) return false;
              return typeof cap === 'object' && 'value' in cap ? !!cap.value : !!cap;
            };

            if (getCapValue('supports5G')) locData.technologies.add('5G');
            if (getCapValue('supports4G')) locData.technologies.add('4G');
            if (getCapValue('supports3G')) locData.technologies.add('3G');
            if (getCapValue('supports2G')) locData.technologies.add('2G');
            if (getCapValue('supportsLTEM')) locData.technologies.add('LTE-M');
            if (getCapValue('supportsNBIoT')) locData.technologies.add('NB-IoT');

            if (getCapValue('supportsVoLTE')) locData.features.add('VoLTE');
            if (getCapValue('supportseSIM')) locData.features.add('eSIM');
            if (getCapValue('supportsVoNR')) locData.features.add('VoNR');
            if (getCapValue('hasGPS')) locData.features.add('GPS');
            if (getCapValue('supportsCarrierAggregation')) locData.features.add('CA');
          }
        }
      }

      const enriched: CapacityLocation[] = data.locations.map((location) => {
        const locData = locationData.get(location.id);
        const sortedTech = locData
          ? Array.from(locData.technologies).sort(
              (a, b) => (TECH_ORDER[a] || 99) - (TECH_ORDER[b] || 99),
            )
          : [];
        const region = data.regions.find((r) => r.id === location.regionId);
        const country = data.countries.find((c) => c.id === location.countryId);
        const ownerOrgId = (location as any).ownerOrgId ?? null;

        return {
          ...location,
          regionName: region?.name || 'Unknown',
          countryName: country?.name || 'Unknown',
          countryFlag: country?.flag || '',
          // Only calculated values from live devices — never stale Firestore counts.
          modemCount: locData?.totalModems ?? 0,
          availableModemCount: locData?.availableModems ?? 0,
          availableNetworks: locData ? Array.from(locData.networks.values()) : [],
          aggregatedCapabilities: locData
            ? {
                technologies: sortedTech,
                features: Array.from(locData.features),
                modemCount: locData.totalModems,
                lastUpdatedAt: new Date().toISOString(),
              }
            : undefined,
          ownership:
            ownerOrgId && activeOrgId && ownerOrgId === activeOrgId ? 'yours' : 'simcheck',
        } as CapacityLocation;
      });

      // Tenancy (#202): other orgs' private locations are never shown, and
      // synthetic demo locations stay scoped to their owning org.
      const tenancyVisible = filterLocationsByTenancy(enriched, activeOrgId, isSuperAdmin, {
        hasDeviceAt: (id) => locationData.has(id),
      });

      // Hide locations with no visible devices. The org's OWN locations stay
      // visible even while empty — a fresh default location must be manageable
      // before any device lands on it.
      const visibleLocations = isSuperAdmin
        ? tenancyVisible
        : tenancyVisible.filter(
            (loc) => locationData.has(loc.id) || (loc as any).ownerOrgId === activeOrgId,
          );

      setLocations(visibleLocations);
      setRegions(data.regions);
      setCountries(data.countries);
      setAllRegions(data.regions);
      setAllCountries(data.countries);
      setLocationsWithDevices(new Set(locationData.keys()));
    } catch (err) {
      console.error('[useNetworkCapacity] Failed to load capacity:', err);
      setError(err instanceof Error ? err.message : 'Failed to load network capacity');
      setLocations([]);
      setRegions([]);
      setCountries([]);
      setAllRegions([]);
      setAllCountries([]);
      setLocationsWithDevices(new Set());
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, isSuperAdmin, visibilityFlags]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId]);

  return useMemo(
    () => ({
      locations,
      regions,
      countries,
      allRegions,
      allCountries,
      locationsWithDevices,
      loading,
      error,
      refresh: load,
    }),
    [
      locations,
      regions,
      countries,
      allRegions,
      allCountries,
      locationsWithDevices,
      loading,
      error,
      load,
    ],
  );
}
